import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { toast, Toaster } from 'sonner';
import { RotateCcw, Download, AlertTriangle, CheckCircle, Loader2, Shield, Database } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export function EmergencyRestore() {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restoring, setRestoring] = useState(null);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('WORLD CUP 2026');

  // Fetch projects and backups
  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    if (selectedProject) {
      fetchBackups();
    }
  }, [selectedProject]);

  const fetchProjects = async () => {
    try {
      const res = await fetch(`${API}/api/projects`);
      const data = await res.json();
      setProjects(data.projects || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const fetchBackups = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API}/api/backups?project=${encodeURIComponent(selectedProject)}`);
      const data = await res.json();
      setBackups(data.backups || []);
    } catch (error) {
      console.error('Error fetching backups:', error);
      toast.error('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (backupId) => {
    if (!window.confirm(
      `⚠️ RESTAURATION D'URGENCE ⚠️\n\n` +
      `Cela va REMPLACER TOUS les tickets du projet "${selectedProject}" par ceux du backup.\n\n` +
      `Êtes-vous sûr de vouloir continuer ?`
    )) return;

    try {
      setRestoring(backupId);
      const res = await fetch(`${API}/api/backups/${backupId}/restore?replace_existing=true`, {
        method: 'POST'
      });
      const data = await res.json();
      
      if (res.ok) {
        toast.success(`Restauration réussie ! ${data.restored_count} tickets restaurés.`);
        // Refresh backups list
        fetchBackups();
      } else {
        throw new Error(data.detail || 'Erreur de restauration');
      }
    } catch (error) {
      toast.error(`Erreur: ${error.message}`);
    } finally {
      setRestoring(null);
    }
  };

  const handleDownload = async (backupId) => {
    try {
      const res = await fetch(`${API}/api/backups/${backupId}/download`);
      if (!res.ok) throw new Error('Erreur de téléchargement');
      
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${backupId}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      
      toast.success('Backup téléchargé !');
    } catch (error) {
      toast.error(`Erreur: ${error.message}`);
    }
  };

  const formatDate = (isoDate) => {
    if (!isoDate) return '-';
    const d = new Date(isoDate);
    return d.toLocaleString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-zinc-950 p-8">
      <Toaster position="top-center" richColors />
      
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Shield className="w-12 h-12 text-red-500" />
            <h1 className="text-3xl font-bold text-white">Restauration d'Urgence</h1>
          </div>
          <p className="text-zinc-400">
            Page accessible sans connexion pour restaurer vos données en cas de problème
          </p>
        </div>

        {/* Warning */}
        <Card className="bg-red-500/10 border-red-500/50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-red-400">
                <p className="font-semibold mb-2">Attention - Mode d'urgence</p>
                <ul className="list-disc list-inside space-y-1 text-red-300">
                  <li>Cette page est réservée aux situations d'urgence</li>
                  <li>La restauration REMPLACE tous les tickets actuels</li>
                  <li>Un backup de sécurité est créé automatiquement avant restauration</li>
                  <li>Retournez à l'application principale dès que possible</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Project Selector */}
        <Card className="bg-zinc-900 border-zinc-700">
          <CardContent className="p-4">
            <label className="block text-sm font-medium text-zinc-400 mb-2">
              Sélectionnez le projet à restaurer
            </label>
            <select
              value={selectedProject}
              onChange={(e) => setSelectedProject(e.target.value)}
              className="w-full bg-zinc-800 border border-zinc-600 rounded-lg px-4 py-2 text-white focus:border-amber-500 focus:outline-none"
            >
              {projects.map(p => (
                <option key={p.name} value={p.name}>{p.name}</option>
              ))}
            </select>
          </CardContent>
        </Card>

        {/* Backups List */}
        <Card className="bg-zinc-900 border-zinc-700">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Database className="w-5 h-5 text-amber-400" />
                <CardTitle className="text-lg text-white">
                  Sauvegardes disponibles - {selectedProject}
                </CardTitle>
              </div>
              <Button
                onClick={fetchBackups}
                variant="outline"
                size="sm"
                disabled={loading}
              >
                <RotateCcw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-amber-400" />
              </div>
            ) : backups.length === 0 ? (
              <div className="text-center py-12 text-zinc-500">
                <Database className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Aucune sauvegarde disponible pour ce projet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {backups.map((backup) => (
                  <div
                    key={backup.backup_id}
                    className="flex items-center justify-between p-4 bg-zinc-800 rounded-lg border border-zinc-700"
                  >
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-mono text-sm text-white">{backup.backup_id}</span>
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          backup.type === 'auto' 
                            ? 'bg-emerald-500/20 text-emerald-400' 
                            : 'bg-blue-500/20 text-blue-400'
                        }`}>
                          {backup.type === 'auto' ? 'Auto' : 'Manual'}
                        </span>
                      </div>
                      <div className="text-xs text-zinc-400">
                        {formatDate(backup.created_at)} • {backup.ticket_count} tickets • 
                        <span className="text-emerald-400 ml-1">{backup.achat_count} ACHAT</span> • 
                        <span className="text-amber-400 ml-1">{backup.vente_count} VENTE</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDownload(backup.backup_id)}
                        className="text-blue-400 border-blue-500/50"
                      >
                        <Download className="w-4 h-4 mr-1" />
                        CSV
                      </Button>
                      
                      <Button
                        size="sm"
                        onClick={() => handleRestore(backup.backup_id)}
                        disabled={restoring === backup.backup_id}
                        className="bg-red-600 hover:bg-red-700 text-white"
                      >
                        {restoring === backup.backup_id ? (
                          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        ) : (
                          <RotateCcw className="w-4 h-4 mr-1" />
                        )}
                        Restaurer
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Back to app link */}
        <div className="text-center">
          <a 
            href="/" 
            className="text-amber-400 hover:text-amber-300 underline"
          >
            ← Retourner à l'application principale
          </a>
        </div>
      </div>
    </div>
  );
}

export default EmergencyRestore;
